/**
 * Customer Billing & budget — shapes and wording (threads/customer-billing-budget).
 * React-free so the sentences the office reads can be tested directly.
 */

export type WorkCategory = "testing" | "repair" | "replacement" | "other";
export type BudgetHealth = "good" | "warn" | "over";

export interface BillingInvoice {
    id: string;
    invoiceNumber: string | null;
    woNumber: string | null;
    jobId: string | null;
    billedDate: string;
    dateSource: "invoice" | "job" | "created";
    imported: boolean;
    status: string;
    workType: string | null;
    category: WorkCategory;
    siteName: string | null;
    totalCents: number;
    balanceCents: number;
    daysLate: number | null;
}

export interface BudgetRule {
    id: string;
    name: string;
    amountCents: number;
    periodStart: string;
    periodEnd: string;
    categories: WorkCategory[];
    alertThresholds: number[];
    alertIncludePending: boolean;
    notes: string | null;
}

export interface BudgetStatus {
    budget: BudgetRule;
    invoicedCents: number;
    draftCents: number;
    pendingCents: number;
    pendingCount: number;
    usedCents: number;
    usedPct: number;
    calendarPct: number;
    remainingCents: number;
    projectedCents: number | null;
    health: BudgetHealth;
    reason: string;
    alertUserIds: string[];
}

export interface PendingQuote {
    id: string;
    number: string | null;
    siteName: string | null;
    title: string | null;
    category: WorkCategory;
    totalCents: number;
    sentAt: string | null;
}

export interface BillingResponse {
    customer: { id: string; name: string | null; fiscalYearStartMonth: number; portalShowBudget?: boolean };
    today: string;
    range: { start: string; end: string };
    presets: Array<{ key: string; label: string; start: string; end: string }>;
    summary: {
        range: { start: string; end: string };
        totals: {
            billedCents: number;
            invoicedCents: number;
            draftCents: number;
            draftCount: number;
            invoiceCount: number;
            owedCents: number;
            owedCount: number;
            overdueCents: number;
            overdueCount: number;
            lastYearSameDateCents: number;
            lastYearTotalCents: number;
        };
        months: Array<{ month: string; cents: number; lastYearCents: number }>;
        byCategory: Array<{ category: WorkCategory; cents: number; count: number }>;
        topSites: Array<{ siteName: string; cents: number; category: WorkCategory }>;
        projectedCents: number | null;
        dataNotes: { datedFromJob: number; datedFromImport: number; uncategorized: number };
    };
    budgets: BudgetStatus[];
    pending: PendingQuote[];
    approvedUnbilled: Array<{ id: string; number: string | null; siteName: string | null; totalCents: number }>;
    invoices: BillingInvoice[];
    categories: Array<{ key: WorkCategory; label: string }>;
    alertPeople: Array<{ id: string; name: string }>;
}

/** Validated categorical palette (dataviz reference instance, slots 1–3) + neutral. */
export const CATEGORY_COLOR: Record<WorkCategory, string> = {
    testing: "#2a78d6",
    repair: "#eb6834",
    replacement: "#1baf7a",
    other: "#8f887c",
};

export const CATEGORY_LABEL: Record<WorkCategory, string> = {
    testing: "Testing",
    repair: "Repair",
    replacement: "Replacement",
    other: "Other",
};

export function money(cents: number, opts: { exact?: boolean } = {}): string {
    const dollars = cents / 100;
    return dollars.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: opts.exact ? 2 : 0,
        maximumFractionDigits: opts.exact ? 2 : 0,
    });
}

export function pct(n: number): string {
    return `${Math.round(n)}%`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function monthLabel(yyyyMm: string): string {
    return MONTHS[Number(yyyyMm.slice(5, 7)) - 1] ?? yyyyMm;
}

export function shortDate(iso: string): string {
    return `${MONTHS[Number(iso.slice(5, 7)) - 1]} ${Number(iso.slice(8, 10))}`;
}

export function longDate(iso: string): string {
    return `${shortDate(iso)}, ${iso.slice(0, 4)}`;
}

export function daysBetween(a: string, b: string): number {
    return Math.round((Date.parse(b + "T00:00:00Z") - Date.parse(a + "T00:00:00Z")) / 86_400_000);
}

/** Share of [start, end] gone by `today`, 0–100. */
export function calendarPct(start: string, end: string, today: string): number {
    const total = daysBetween(start, end) + 1;
    return Math.max(0, Math.min(100, ((daysBetween(start, today) + 1) / total) * 100));
}

/** A budget's bar colour: its first category's, or brown for "everything". */
export function budgetColor(b: BudgetRule): string {
    return b.categories.length === 0 ? "#6b4f1d" : CATEGORY_COLOR[b.categories[0]!];
}

/** Budgets that exactly span the range add up to "the budget" for the headline. */
export function budgetsForRange(budgets: BudgetStatus[], range: { start: string; end: string }): BudgetStatus[] {
    return budgets.filter((b) => b.budget.periodStart === range.start && b.budget.periodEnd === range.end);
}

/**
 * The one sentence at the top of the tab.
 *   With budgets:    "Fort Worth ISD has used 18% of its $306,000 with 75% of the school year still ahead."
 *   Without:         "Fort Worth ISD has been billed $54,055 so far this school year — $58,162 at this point last year."
 *   Past range:      "Fort Worth ISD was billed $286,661 in school year 2025–26."
 */
export function headline(d: BillingResponse, customerShortName: string): { text: string; health: BudgetHealth | null } {
    const t = d.summary.totals;
    const running = d.today >= d.range.start && d.today <= d.range.end;
    const yearWord = d.customer.fiscalYearStartMonth === 1 ? "year" : "school year";
    const spanning = budgetsForRange(d.budgets, d.range);
    if (running && spanning.length > 0) {
        const cap = spanning.reduce((a, b) => a + b.budget.amountCents, 0);
        const used = spanning.reduce((a, b) => a + b.usedCents, 0);
        const left = 100 - calendarPct(d.range.start, d.range.end, d.today);
        // The whole account is only as calm as its least calm budget.
        const health: BudgetHealth = spanning.some((b) => b.health === "over")
            ? "over"
            : spanning.some((b) => b.health === "warn")
              ? "warn"
              : "good";
        return {
            text: `${customerShortName} has used ${pct((used / cap) * 100)} of its ${money(cap)} with ${pct(left)} of the ${yearWord} still ahead.`,
            health,
        };
    }
    if (running) {
        const ly = t.lastYearSameDateCents > 0 ? ` — ${money(t.lastYearSameDateCents)} at this point last year` : "";
        return { text: `${customerShortName} has been billed ${money(t.billedCents)} so far${ly}.`, health: null };
    }
    return {
        text: `${customerShortName} was billed ${money(t.billedCents)} from ${longDate(d.range.start)} to ${longDate(d.range.end)}.`,
        health: null,
    };
}

/** "Fort Worth Independent School District" → "Fort Worth ISD"; short names pass through. */
export function shortCustomerName(name: string | null): string {
    const n = (name ?? "This customer").trim();
    return n.replace(/Independent School District/i, "ISD").replace(/\s+/g, " ");
}

// ── Budgets everywhere: chip, send-time impact, Money → Budgets ─────────────

/** Mirror of the server's customerBillingMath.workCategory — keep the two in step. */
export function workCategory(name: string | null | undefined): WorkCategory {
    const n = (name ?? "").toLowerCase();
    if (!n.trim()) return "other";
    if (/replac|install|new device/.test(n)) return "replacement";
    if (/repair|leak|flush|clean|rebuild|fix/.test(n)) return "repair";
    if (/\btest|inspection|annual/.test(n)) return "testing";
    return "other";
}

/** Mirror of the server's pendingCategory: a quote is repair work unless it says otherwise. */
export function quoteCategory(title: string | null | undefined, workType?: string | null): WorkCategory {
    const fromTitle = workCategory(title);
    if (fromTitle === "repair" || fromTitle === "replacement") return fromTitle;
    const fromJob = workCategory(workType);
    if (fromJob === "repair" || fromJob === "replacement") return fromJob;
    return fromTitle === "testing" ? "testing" : "repair";
}

export function inCategories(category: WorkCategory, categories: WorkCategory[]): boolean {
    return categories.length === 0 || categories.includes(category);
}

/** One live budget, as GET /api/budgets/by-customer returns it (riskiest first per customer). */
export interface ChipBudget {
    budgetId: string;
    name: string;
    categories: WorkCategory[];
    amountCents: number;
    usedCents: number;
    usedPct: number;
    calendarPct: number;
    remainingCents: number;
    pendingCents: number;
    pendingCount: number;
    periodStart: string;
    periodEnd: string;
    health: BudgetHealth;
    reason: string;
}

export interface WatchBudget extends ChipBudget {
    customerId: string;
    customerName: string | null;
    live: boolean;
    projectedCents: number | null;
    invoicedCents: number;
    draftCents: number;
}

const HEALTH_RANK: Record<BudgetHealth, number> = { over: 0, warn: 1, good: 2 };

export function worstHealth(hs: BudgetHealth[]): BudgetHealth {
    return hs.reduce<BudgetHealth>((w, h) => (HEALTH_RANK[h] < HEALTH_RANK[w] ? h : w), "good");
}

/**
 * What the chip says. With a category (a job, a quote) it names the one budget
 * that work counts against; without one it totals every live budget.
 * Null means "show nothing" — no budget, or none covering this kind of work.
 */
export function chipSummary(
    budgets: ChipBudget[],
    opts: { category?: WorkCategory; compact?: boolean } = {},
): { text: string; health: BudgetHealth; usedPct: number; budgets: ChipBudget[] } | null {
    const scoped = opts.category ? budgets.filter((b) => inCategories(opts.category!, b.categories)) : budgets;
    if (scoped.length === 0) return null;
    if (opts.category) {
        const b = [...scoped].sort((x, y) => HEALTH_RANK[x.health] - HEALTH_RANK[y.health])[0]!;
        const text = opts.compact
            ? `${b.name} ${pct(b.usedPct)}`
            : b.remainingCents < 0
              ? `${money(-b.remainingCents)} over in ${b.name}`
              : `${money(b.remainingCents)} left in ${b.name}`;
        return { text, health: b.health, usedPct: b.usedPct, budgets: [b] };
    }
    const amount = scoped.reduce((s, b) => s + b.amountCents, 0);
    const used = scoped.reduce((s, b) => s + b.usedCents, 0);
    const usedPct = amount > 0 ? (used / amount) * 100 : 0;
    const left = amount - used;
    const health = worstHealth(scoped.map((b) => b.health));
    const lead = left < 0 ? `${money(-left)} over` : `${money(left)} left`;
    return { text: opts.compact ? lead : `${lead} · ${pct(usedPct)} used`, health, usedPct, budgets: scoped };
}

export interface ImpactLine {
    budgetId: string;
    name: string;
    amountCents: number;
    categories: WorkCategory[];
    usedCents: number;
    afterCents: number;
    pctBefore: number;
    pctAfter: number;
    leftAfterCents: number;
    calendarPct: number;
    health: BudgetHealth;
    alreadyCounted: boolean;
}

export interface Impact {
    customerId: string;
    customerName: string | null;
    kind: "quote" | "invoice";
    category: WorkCategory;
    cents: number;
    lines: ImpactLine[];
}

/**
 * The one line shown before a quote or packet goes out. Informs, never blocks.
 * An invoice already sits inside "used" (drafts count), so its before-figure
 * backs it out to show the step this invoice takes.
 */
export function impactSentence(
    impact: Impact,
    line: ImpactLine,
    customerShortName: string,
): { text: string; sub: string; health: BudgetHealth } {
    const before = line.alreadyCounted ? line.usedCents - impact.cents : line.usedCents;
    const after = line.afterCents;
    const afterPct = line.amountCents > 0 ? (after / line.amountCents) * 100 : 0;
    const beforePct = line.amountCents > 0 ? (before / line.amountCents) * 100 : 0;
    const health: BudgetHealth =
        line.leftAfterCents < 0 ? "over" : afterPct >= 90 || line.health !== "good" ? "warn" : "good";
    if (line.leftAfterCents < 0) {
        return {
            text: `This would put ${line.name} ${money(-line.leftAfterCents)} over its ${money(line.amountCents)} cap.`,
            sub:
                impact.kind === "quote"
                    ? `Send it anyway if ${customerShortName} should decide, or add a note to the quote first.`
                    : `${customerShortName} may push back — worth a note in the email.`,
            health,
        };
    }
    const text =
        impact.kind === "quote"
            ? `${line.name}: ${pct(beforePct)} → ${pct(afterPct)} if ${customerShortName} approves.`
            : `Counts toward ${line.name}: ${money(before)} → ${money(after)} (${pct(afterPct)}).`;
    const room =
        health === "good" ? "plenty of room" : afterPct >= 90 ? "getting close to the cap" : "ahead of the calendar";
    return { text, sub: `${money(line.leftAfterCents)} left after this one · ${room}`, health };
}

// ── ⌘K answers (GET /api/budgets/ask) ───────────────────────────────────────

export interface CustomerAnswer {
    kind: "customer";
    customerId: string;
    customerName: string | null;
    headline: string;
    sentence: string;
    detail: string | null;
    health: BudgetHealth | null;
    range: { start: string; end: string };
    categoryLabel: string | null;
    links: { tab: string; xlsx: string };
}
export interface OverAnswer {
    kind: "over";
    headline: string;
    items: Array<{ customerId: string; customerName: string; budgetName: string; text: string; health: BudgetHealth }>;
    link: string;
}
export type BudgetAnswer = CustomerAnswer | OverAnswer;
